import { NumericFormat } from 'react-number-format';
import { CarketaDisclosure } from 'src/@carketa/sales-packet/components/CarketaDisclosure';
import { cn } from 'src/utils/@/lib/utils';

type VehicleSalesPacketProps = {
  neoVin: any;
  pdfMode?: boolean;
};

export default function WindowStickerWeb({
  neoVin,
  pdfMode,
}: VehicleSalesPacketProps) {
  const formattedNeoVinDataSample =
    neoVin?.neovin && neoVin?.neovin.generic
      ? neoVin?.neovin.generic[0].USA[0]
      : neoVin?.neovin;

  if (!neoVin) {
    return <>Loading....</>;
  }

  if (
    !formattedNeoVinDataSample ||
    neoVin?.neovin.code ||
    Object.keys(neoVin?.neovin).length === 0
  ) {
    return (
      <>
        <div className="flex h-full w-full items-start justify-center">
          No Data found on this VIN.
        </div>
      </>
    );
  }

  if (!formattedNeoVinDataSample.installed_options_details) {
    formattedNeoVinDataSample.installed_options_details = [];
  }

  const { name: exteriorColor } = formattedNeoVinDataSample.exterior_color || {
    name: '-',
  };
  const { name: interiorColor } = formattedNeoVinDataSample.interior_color || {
    name: '-',
  };

  const vehicleName = () => {
    return `${formattedNeoVinDataSample.year} ${
      formattedNeoVinDataSample.make
    } ${formattedNeoVinDataSample.model} ${
      formattedNeoVinDataSample.engine ?? ''
    } ${formattedNeoVinDataSample.transmission}`;
  };

  const featureLineLength = 48;
  const categoryLineLength = 22;
  const bottomLines = 20;
  const optionsLineLength = 34;

  const standardEquips = [];
  const categories = [] as any;

  if (
    formattedNeoVinDataSample?.features &&
    formattedNeoVinDataSample?.features?.STANDARD &&
    formattedNeoVinDataSample?.features?.STANDARD.length !== 0
  ) {
    const flattenFeatures = (featuresObj: { [key: string]: any[] }): any[] => {
      return Object.values(featuresObj).flat();
    };

    formattedNeoVinDataSample.features = flattenFeatures(
      formattedNeoVinDataSample?.features
    );
    formattedNeoVinDataSample.installed_equipment = flattenFeatures(
      formattedNeoVinDataSample?.installed_equipment
    );
  }

  if (
    formattedNeoVinDataSample?.features &&
    formattedNeoVinDataSample?.features?.length !== 0
  ) {
    for (let i = 0; i < formattedNeoVinDataSample.features.length; i++) {
      const feature = formattedNeoVinDataSample.features[i];
      if (standardEquips.length === 0) {
        standardEquips.push({
          category: feature.category,
          categoryLines: Math.ceil(
            feature.category.length / categoryLineLength
          ),
          descriptions: [
            {
              description: feature.description,
              descriptionLines: Math.ceil(
                feature.description.length / featureLineLength
              ),
            },
          ],
          totalLines:
            Math.ceil(feature.description.length / featureLineLength) +
            Math.ceil(feature.category.length / categoryLineLength),
        });
        categories.push(feature.category);
      } else {
        if (categories.includes(feature.category)) {
          //@ts-ignore
          const sec: any = standardEquips.find(
            (e: any) => e.category === feature.category
          );
          sec.descriptions.push({
            description: feature.description,
            descriptionLines: Math.ceil(
              feature.description.length / featureLineLength
            ),
          });
          sec.totalLines =
            sec.totalLines +
            Math.ceil(feature.description.length / featureLineLength);
        } else {
          categories.push(feature.category);
          standardEquips.push({
            category: feature.category,
            categoryLines: Math.ceil(
              feature.category.length / categoryLineLength
            ),
            descriptions: [
              {
                description: feature.description,
                descriptionLines: Math.ceil(
                  feature.description.length / featureLineLength
                ),
              },
            ],
            totalLines:
              Math.ceil(feature.description.length / featureLineLength) +
              Math.ceil(feature.category.length / categoryLineLength),
          });
        }
      }
    }
  } else {
    standardEquips.push({ category: 'No Data', descriptions: [''] });
  }

  standardEquips.sort((a: any, b: any) => a.category.localeCompare(b.category));

  const totalFeatureLines = standardEquips.reduce((acc: any, curr: any) => {
    return acc + curr.totalLines;
  }, 0);

  //a count for size of the page?
  const totalOptionLines =
    formattedNeoVinDataSample.installed_options_details.reduce(
      (acc: any, curr: any) => {
        return acc + Math.ceil(curr.name.length / optionsLineLength);
      },
      0
    );

  const totalColumnLines = Math.ceil(
    (totalFeatureLines + totalOptionLines + bottomLines) / 3
  );

  const getColumnContent = (standardEquips: any, totalColumnLines: number) => {
    const columnContent: any = [[], [], []];
    let currentColumn = 0;
    let linesUsed = 0;

    standardEquips.map((standardEquip: any) => {
      if (linesUsed + standardEquip.totalLines <= totalColumnLines) {
        columnContent[currentColumn].push({
          type: 'category',
          value: standardEquip.category,
        });
        standardEquip.descriptions.map((description: any) => {
          columnContent[currentColumn].push({
            type: 'description',
            value: description.description,
          });
        });
        linesUsed += standardEquip.totalLines;
      } else {
        if (linesUsed + standardEquip.categoryLines <= totalColumnLines) {
          columnContent[currentColumn].push({
            type: 'category',
            value: standardEquip.category,
          });
          linesUsed += standardEquip.categoryLines;
        } else {
          currentColumn++;
          linesUsed = 0;
          columnContent[currentColumn].push({
            type: 'category',
            value: standardEquip.category,
          });
          linesUsed += standardEquip.categoryLines;
        }
        standardEquip.descriptions.map((description: any) => {
          if (linesUsed + description.descriptionLines <= totalColumnLines) {
            columnContent[currentColumn].push({
              type: 'description',
              value: description.description,
            });
            linesUsed += description.descriptionLines;
          } else {
            currentColumn++;
            linesUsed = 0;
            columnContent[currentColumn].push({
              type: 'description',
              value: description.description,
            });
            linesUsed += description.descriptionLines;
          }
        });
      }
    });
    return columnContent;
  };

  const columnContent = getColumnContent(standardEquips, totalColumnLines);

  const standardEquips1 = columnContent[0];
  const standardEquips2 = columnContent[1];
  const standardEquips3 = columnContent[2];

  const standardEquipWidget = (standardEquipList: any) => {
    return standardEquipList.map((standardEquip: any, index: any) => {
      if (standardEquip.type === 'category') {
        return (
          <div key={index}>
            <p className="text-xs font-bold pb-1">
              {standardEquip.value.toUpperCase()}
            </p>
          </div>
        );
      } else {
        return (
          <div key={index}>
            <p className="text-xs"> • {standardEquip.value}</p>
          </div>
        );
      }
    });
  };

  const formatPrice = (value: any) => {
    return (
      <NumericFormat
        disabled={true}
        className="w-[90%]"
        value={value}
        allowLeadingZeros
        thousandSeparator=","
        prefix="$"
      />
    );
  };

  const installedOPtionsMsrpWidget = (optionDetails: any) => {
    return (
      <div className="pb-2">
        {optionDetails &&
          optionDetails.map((optionDetail: any, index: any) => {
            return (
              <div key={index}>
                <p className="text-xs  pb-1">
                  - {optionDetail.name.toUpperCase()}
                </p>
              </div>
            );
          })}
      </div>
    );
  };

  //combined MPG
  const combinedMpgAvg =
    (formattedNeoVinDataSample.city_mpg +
      formattedNeoVinDataSample.highway_mpg) /
    2;

  const combinedMpgInitialValue = { value: combinedMpgAvg.toString() };
  let combinedMpg;

  if (formattedNeoVinDataSample.installed_equipment) {
    combinedMpg = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) => element.attribute === 'combined (mpg)'
    );
    if (!combinedMpg) {
      combinedMpg = combinedMpgInitialValue;
    }
  } else {
    combinedMpg = combinedMpgInitialValue;
  }

  // Gallon per 100 miles
  const gallonPer100MilesInitialValue = { value: '0' };
  let gallonPer100Miles;

  if (formattedNeoVinDataSample.installed_equipment) {
    gallonPer100Miles = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) => element.attribute === 'combined (l/100km)'
    );
    if (!gallonPer100Miles) {
      gallonPer100Miles = gallonPer100MilesInitialValue;
    }
  } else {
    gallonPer100Miles = gallonPer100MilesInitialValue;
  }

  //Government overall rating
  const overAllRatingInitialValue = { value: '0' };
  let overAllRating;

  if (formattedNeoVinDataSample.installed_equipment) {
    overAllRating = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) =>
        element.attribute === 'overall' && element.location === 'NHTSA'
    );
    if (!overAllRating) {
      overAllRating = overAllRatingInitialValue;
    }
  } else {
    overAllRating = overAllRatingInitialValue;
  }

  //Government frontal crash rating
  const frontalCrashRatingInitialValue = { value: '0' };
  let frontalCrashRating;

  if (formattedNeoVinDataSample.installed_equipment) {
    frontalCrashRating = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) =>
        element.attribute === 'frontal crash rating' &&
        element.location === 'NHTSA'
    );
    if (!frontalCrashRating) {
      frontalCrashRating = frontalCrashRatingInitialValue;
    }
  } else {
    frontalCrashRating = frontalCrashRatingInitialValue;
  }
  // Government rollover rating
  const rollOverRatingInitialValue = { value: '0' };
  let rollOverRating;

  if (formattedNeoVinDataSample.installed_equipment) {
    rollOverRating = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) =>
        element.attribute === 'rollover rating' && element.location === 'NHTSA'
    );
    if (!rollOverRating) {
      rollOverRating = rollOverRatingInitialValue;
    }
  } else {
    rollOverRating = rollOverRatingInitialValue;
  }

  //Government rollover rating
  const sideCrashRatingInitialValue = { value: '0' };
  let sideCrashRating;

  if (formattedNeoVinDataSample.installed_equipment) {
    sideCrashRating = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) =>
        element.attribute === 'side impact' && element.location === 'NHTSA'
    );
    if (!sideCrashRating) {
      sideCrashRating = sideCrashRatingInitialValue;
    }
  } else {
    sideCrashRating = sideCrashRatingInitialValue;
  }
  //EMISSION
  const emissionInitialValue = { name: '' };
  let emission;

  if (formattedNeoVinDataSample.installed_equipment) {
    emission = formattedNeoVinDataSample.installed_equipment.find(
      (element: any) => element.code === 'FE'
    );
    if (!emission) {
      emission = emissionInitialValue;
    }
  } else {
    emission = emissionInitialValue;
  }

  const annualMileage = 15000;
  const pricePerGallon = 3.55;
  const totalOver5Years = 11500;

  const annualFuelCost =
    (annualMileage / parseFloat(combinedMpg.value)) * pricePerGallon;

  const youSaveOrSpend = () => {
    const youSaveOrSpendValue = (Math.round(annualFuelCost) * 5 -
      totalOver5Years) as any;
    const youSaveText = 'You SAVE';
    const youSpendText = 'You SPEND';

    const youSaveSubText =
      'in fuel costs over 5 years compared to average new vehicle.';
    const youSpendSubText =
      'more in fuel costs over 5 years compared to the average new vehicle.';

    return (
      <div className="flex-1 justify-start">
        <div className="flex h-8 items-center">
          <img className="w-4 h-4" src={'/img/flag-solid.svg'} alt="" />
          <p className="text-md pl-2 font-medium">
            {youSaveOrSpendValue > 0 ? youSpendText : youSaveText}
          </p>
        </div>
        <p className="text-lg font-bold pb-2">
          {isNaN(youSaveOrSpendValue)
            ? 'N/A'
            : formatPrice(parseInt(Math.abs(youSaveOrSpendValue).toString()))}
        </p>
        <p className="text-xs ">
          {youSaveOrSpendValue > 0 ? youSpendSubText : youSaveSubText}
        </p>
      </div>
    );
  };
  const ratingWidget = (ratingValue: any) => {
    const elements = [];

    const itemsCount =
      ratingValue === '0' || !ratingValue
        ? 0
        : parseInt(parseFloat(ratingValue.toString()).toString());

    for (let i = 1; i <= 5; i++) {
      if (i <= itemsCount) {
        elements.push(<span key={i}>★</span>);
      } else {
        elements.push(<span key={i}>☆</span>);
      }
    }
    return <div className="flex">{elements}</div>;
  };

  return (
    <div>
      <div className={cn('flex px-2', pdfMode ? 'flex-row' : 'flex-col lg:flex-row')}>
        <div className="flex-auto lg:pr-4">
          <div className="flex justify-between">
            <div className="flex items-center w-full">
              <div className="mr-8 w-[180px] lg:w-[200px]">
                <img
                  src={`https://storage.googleapis.com/carketa-next-assets/vehicle-logos/${formattedNeoVinDataSample.make.toLowerCase()}-logo.jpeg`}
                  alt=""
                />
              </div>
              <div className="flex w-full flex-col">
                <div className="flex flex-col">
                  <h1 className="text-lg font-bold ">{vehicleName()}</h1>
                  <p className="text-sm font-semibold">
                    VIN: {formattedNeoVinDataSample.vin}
                  </p>
                </div>
                <div className="flex flex-col py-4 md:flex-row">
                  <div className="mr-5">
                    <div className="flex justify-start">
                      <p className="w-[112px] text-xs text-black md:w-20">EXTERIOR: </p>
                      <p className="text-xs">{exteriorColor}</p>
                    </div>
                    <div className="flex justify-start">
                      <p className="w-[112px] text-xs text-black md:w-20">INTERIOR: </p>
                      <p className="text-xs">{interiorColor}</p>
                    </div>
                    <div className="flex justify-start">
                      <p className="w-[112px] text-xs text-black md:w-20">ENGINE: </p>
                      <p className="text-xs">
                        {formattedNeoVinDataSample.engine}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-start">
                      <p className="text-xs w-28 ">TRANSMISSION: </p>
                      <p className="text-xs">
                        {formattedNeoVinDataSample.transmission}
                      </p>
                    </div>
                    <div className="flex justify-start">
                      <p className="text-xs w-28">DRIVE TRAIN: </p>
                      <p className="text-xs">
                        {formattedNeoVinDataSample.drivetrain}
                      </p>
                    </div>
                    <div className="flex justify-start">
                      <p className="text-xs w-28 ">STYLE: </p>
                      <p className="text-xs">
                        {formattedNeoVinDataSample.body_type}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="w-fit rounded border-2 border-black px-4 py-2"
            id="standard-equipment-container"
          >
            <h1 className="text-md font-bold">Standard Equipment</h1>
            <div className="flex pt-2">
              <div className="flex-1 ">
                {standardEquipWidget(standardEquips1)}
              </div>
              <div className="border-l-2 my-2 border-gray-800 w-1" />
              <div className="flex-1 pl-4 ">
                {standardEquipWidget(standardEquips2)}
              </div>
              <div className="border-l-2 my-2 border-gray-800 w-1" />
              <div className="flex-1 pl-4">
                {standardEquipWidget(standardEquips3)}
                <div className="mx-2 mt-3">
                  <p className="text-xs italic pb-2">
                    +For more information, see dealer, owner&apos;s manual
                  </p>
                  <p className="text-xs italic">
                    ++Optional Equipment Replaces Standard Features
                  </p>
                </div>
                <div className="flex justify-between pt-2">
                  <p className="text-xs font-bold w-40">
                    Manufacturer&apos;s Suggested Retail Price:{' '}
                    {formatPrice(formattedNeoVinDataSample.msrp)}
                  </p>
                </div>
                <div className="flex justify-between py-4">
                  <p className="text-xs font-bold">
                    Options Included by Manufacturer:{' '}
                    {formatPrice(
                      formattedNeoVinDataSample.installed_options_msrp
                    )}
                  </p>
                </div>
                {installedOPtionsMsrpWidget(
                  formattedNeoVinDataSample.installed_options_details
                )}
                <div className="border-t-2 my-2 border-gray-800 w-full " />
                <div className="flex justify-between">
                  <p className="text-xs font-bold">
                    DESTINATION CHARGES:{' '}
                    {formatPrice(formattedNeoVinDataSample.delivery_charges)}
                  </p>
                </div>
                <div className="border-t-2 my-2  border-gray-800 w-full " />
                <div className="flex justify-between pt-2">
                  <p className="text-xs font-bold">
                    TOTAL:{' '}
                    {formatPrice(formattedNeoVinDataSample.combined_msrp)}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs italic pt-4">
                * Does not include dealer installed options and accessories,
                local taxes, or license fees. This label has been applied
                pursuant to federal law. Do not remove prior to delivery to the
                ultimate purchaser
              </p>
              <p className="text-xs italic pt-4">
                ** Not actual Monroney Label. Provided for informational
                purposes only. Vehicle features, pricing and other information
                subject to change, many vary from actual vehicle content and/or
                Monroney label description. While{' '}
                {formattedNeoVinDataSample.make} makes reasonable efforts to
                ensure accuracy of information, {formattedNeoVinDataSample.make}{' '}
                is not responsible for changes, errors or omissions. See actual
                vehicle and/or verify information in question with your{' '}
                {formattedNeoVinDataSample.make} dealer.
              </p>
            </div>
          </div>
        </div>
        <div className="left-section pt-3">
          <div className="flex justify-between bg-stone-800 rounded px-3 py-2">
            <div className="flex justify-start items-center ">
              <div className="">
                <p className="pb-px text-xs font-bold text-white">
                  EPA
                </p>
                <div className="border-t  border-white-800 w-full " />
                <p className="pt-px text-xs font-bold text-white">
                  DOT
                </p>
              </div>
              <p className="text-md font-bold text-white pl-3">
                Fuel Economy and Environment
              </p>
            </div>
            <div className="flex bg-stone-50 rounded p-2 items-center">
              <img className="w-4 h-4" src={'/img/gas-pump-solid.svg'} alt="" />
              <p className="text-xs pl-2"> Gasoline Vehicle</p>
            </div>
          </div>
          <div className="flex border-2 items-center border-black rounded px-4 py-2 mt-2 flex-auto">
            <div className="flex-1">
              <div className="flex h-8 items-center">
                <img
                  className="w-4 h-4"
                  src={'/img/gas-pump-solid.svg'}
                  alt=""
                />
                <p className="text-sm pl-3 font-semibold"> Fuel Company</p>
              </div>
              <div className="flex justify-start items-center">
                <h1 className="text-6xl font-bold">
                  {combinedMpg.value !== 'NaN'
                    ? Math.round(combinedMpg.value)
                    : 'N/A'}
                </h1>
                <div className="justify-start pl-3">
                  <h1 className="font-bold text-md">MPG</h1>
                  <div className="flex ">
                    <div className="pr-4">
                      <p className="text-xs">
                        {formattedNeoVinDataSample.city_mpg
                          ? Math.round(formattedNeoVinDataSample.city_mpg)
                          : 'N/A'}
                      </p>
                      <p className="text-xs">City</p>
                    </div>
                    <div>
                      <p className="text-xs">
                        {formattedNeoVinDataSample.highway_mpg
                          ? Math.round(formattedNeoVinDataSample.highway_mpg)
                          : 'N/A'}
                      </p>
                      <p className="text-xs">Highway</p>
                    </div>
                  </div>
                </div>
              </div>
              {gallonPer100Miles.value !== '0' ? (
                <div className="flex h-8 items-center">
                  <p className="text-lg font-bold">
                    {(parseFloat(gallonPer100Miles.value) * 0.4251).toFixed(1)}
                  </p>
                  <p className="text-xs pl-2">Gallons per 100 miles</p>
                </div>
              ) : (
                <div className="h-8"></div>
              )}
            </div>
            {youSaveOrSpend()}
          </div>
          <div className="flex">
            <div className="flex-none w-full border-2 items-center border-black rounded px-2 py-3 mt-2 mr-2">
              <p className="text-sm font-medium text-start">Annual Fuel COST</p>
              <p className="text-4xl font-bold text-start">
                {isNaN(annualFuelCost)
                  ? 'N/A'
                  : formatPrice(Math.round(annualFuelCost))}
              </p>
              <div className="flex flex-col mt-2">
                <div className="mr-2 flex-none pr-2 mt-1">
                  <p className="font-bold text-xs ">
                    Fuel Economy & Greenhouse Gas Rating{' '}
                    <span className="font-normal text-xs">(Tailpipe only)</span>
                  </p>
                  {/* {linearRatingBar()} */}
                  N/A
                </div>
                <div className="mr-2 flex-none pr-2 mt-1">
                  <p className="font-bold text-xs">
                    Smog Rating{' '}
                    <span className="font-normal text-xs">(Tailpipe only)</span>
                  </p>
                  {/* {linearRatingBar()} */}
                  N/A
                </div>
              </div>
            </div>
          </div>
          <div className="flex bg-stone-800 rounded py-4 px-4 mt-2">
            <div className="flex-1 justify-start items-center pr-4">
              <p className="text-xs font-semibold text-white pb-3">
                Actual results will vary from many reasons, including driving
                conditions and how you drive and maintain your vehicle. The
                average new vehicle gets 27 MPG and costs $7,000 to fuel over 5
                years. Cost estimates are based on 15,000 miles per year at
                $3.55 per gallon. MPGe is miles per gasoline gallon equivalent.
              </p>
              <p className="text-sm font-bold text-white ">fueleconomy.gov</p>
              <p className="text-xs font-bold text-white ">
                Calculate personalized estimates and compare vehicles
              </p>
            </div>
          </div>
          <div className="border-2 border-black rounded mt-2">
            <div className="flex bg-stone-800  px-3 py-2 ">
              <div className="flex-1 justify-start items-center pr-2">
                <p className="text-sm font-semibold text-white ">
                  GOVERNMENT 5-STAR SAFETY RATINGS
                </p>
              </div>
              <div className="flex justify-start w-48 pl-1">
                <p className="text-sm text-white font-medium">DELIVERY</p>
              </div>
            </div>
            <div className="flex ">
              <div className="flex-1">
                <div className="py-2 px-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 mr-4">
                      <p className="text-xs font-bold pb-2">
                        Overall Vehicle Score
                      </p>
                      <p className="text-xs">
                        Based on the combined ratings of frontal, side and
                        rollover, Should ONLY be compared to the vehicles of
                        similar size and weight
                      </p>
                    </div>
                    <div className="w-30 mt-1">
                      {ratingWidget(overAllRating.value)}
                    </div>
                  </div>
                  <div className="border-t-2 my-2  border-gray-800 w-full " />
                  <div className="flex justify-between">
                    <div className="flex-1 mr-4">
                      <p className="text-xs font-bold pb-2">Frontal Crash</p>
                      <p className="text-xs">
                        Based on the risk of injury in a frontal impact. Should
                        ONLY be compared to the vehicles of similar size and
                        weight.
                      </p>
                    </div>
                    <div className="w-30 mt-1">
                      {ratingWidget(frontalCrashRating.value)}
                    </div>
                  </div>
                  <div className="border-t-2 my-2  border-gray-800 w-full " />
                  <div className="flex justify-between">
                    <div className="flex-1 mr-4">
                      <p className="text-xs font-bold pb-2">Side Crash</p>
                      <p className="text-xs">
                        Based on the risk of injury in a side impact.
                      </p>
                    </div>
                    <div className="w-30 mt-1">
                      {ratingWidget(sideCrashRating.value)}
                    </div>
                  </div>
                  <div className="border-t-2 my-2  border-gray-800 w-full " />
                  <div className="flex justify-between">
                    <div className="flex-1 mr-4">
                      <p className="text-xs font-bold pb-2">Rollover</p>
                      <p className="text-xs">
                        Based on the risk of rollover in a single-vehicle crash.
                      </p>
                    </div>
                    <div className="w-30 mt-1">
                      {ratingWidget(rollOverRating.value)}
                    </div>
                  </div>
                </div>
                <div className="flex bg-stone-800 mt-2">
                  <div className="flex-1 justify-start items-center p-4">
                    <p className="text-xs font-semibold text-white">
                      Star ratings range from 1 to 5 stars (★★★★★) with 5 being
                      the highest.
                    </p>
                    <p className="text-xs font-bold text-white ">
                      Source: National Highway Traffic Safety Administration
                      (NHTSA)
                    </p>
                    <p className="text-xs font-bold text-white ">
                      www.safercar.gov or 1-888-327-4236
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <p className=" text-xs pb-3">
                    This Vehicle qualifies for {formattedNeoVinDataSample.make}
                    &apos;s
                  </p>
                  <p className="font-bold text-sm pb-2">
                    Security + Plus Extended Protection Plan
                  </p>
                  <p className=" text-xs">
                    {' '}
                    The only service agreement backed by{' '}
                    {formattedNeoVinDataSample.make} Extended Services North
                    America! Ask your dealer for details, or call 1-800-
                    {formattedNeoVinDataSample.make}-1 for more information
                  </p>
                </div>
              </div>
              <div className="border-l-2  border-gray-800 w-1" />
              <div className=" justify-start  mx-4 my-2">
                <p className="font-bold text-xs pb-2">VEHICLE COLORS:</p>
                <p className="font-semibold text-xs">
                  EXT:{' '}
                  <span className="ml-5 font-normal text-xs">
                    {exteriorColor}
                  </span>
                </p>
                <p className="font-semibold text-xs">
                  INT:{' '}
                  <span className="ml-5 font-normal text-xs">
                    {interiorColor}
                  </span>
                </p>
                <p className="font-semibold text-xs pt-4">
                  VIN:{' '}
                  <span className="font-normal text-xs">
                    {formattedNeoVinDataSample.vin}
                  </span>
                </p>
                <p className="font-semibold text-xs">
                  EMS:{' '}
                  <span className="font-normal text-xs">
                    {emission.name.toUpperCase()}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={cn('pt-[5px]', !pdfMode && 'hidden')}>
        <CarketaDisclosure />
      </div>
    </div>
  );
}
